import {
  Badge,
  Banner,
  Button,
  Empty,
  Input,
  Loader,
  Select,
  Surface,
  Switch,
  Table,
  Tabs,
  Text,
} from '@cloudflare/kumo'
import { ArrowsClockwise, Receipt } from '@phosphor-icons/react'
import {
  productCategoryLabel,
  QUOTE_SLA_DAYS,
  quoteMatchesPaymentSource,
  type AdminQuoteView,
  type QuotePaymentSource,
  type QuoteStatus,
} from '@repo/shared'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Navigate, useNavigate, useSearchParams } from 'react-router'
import { adminApi } from '../api'
import { HelpPanel } from '../components/help-panel'
import { PageHeader } from '../components/page-header'
import {
  PAYMENT_SOURCE_ITEMS,
  PaymentSourceBadge,
  QuoteCloseBadge,
} from '../components/payment-source-badge'
import { useGetToken } from '../lib/auth'
import { fmtInt, fmtMoney, fmtTime, toMessage } from '../lib/format'
import { AgeBadge, STATUS_BADGE, STATUS_LABEL } from '../lib/quote-labels'
import { refreshSummary } from '../lib/summary'
import { TablePagination } from '../lib/table-pagination'
import { usePagedList } from '../lib/use-page'

/** 공장 견적 중 가장 낮은 단가 (없으면 null) */
function lowestOffer(q: AdminQuoteView) {
  return q.offers.reduce<AdminQuoteView['offers'][number] | null>(
    (min, o) => (min === null || o.unitPrice < min.unitPrice ? o : min),
    null,
  )
}

/**
 * 견적 요청 목록 — 상태 탭 · 지연 필터 · 검색. 화면 폭을 전부 쓴다.
 * 요청을 누르면 상세 페이지(/quotes/:id)로 가서 스펙시트 확인 · 공장 견적 입력 · 마감을 한다.
 */
export function QuotesPage() {
  const getToken = useGetToken()
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const [items, setItems] = useState<AdminQuoteView[] | null>(null)
  const [tab, setTab] = useState<'all' | QuoteStatus>('all')
  const [overdueOnly, setOverdueOnly] = useState(false)
  const [source, setSource] = useState<QuotePaymentSource | 'all'>('all')
  const [search, setSearch] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const reload = useCallback(async () => {
    setBusy(true)
    try {
      const { items } = await adminApi.quotes.list(getToken)
      setItems(items)
      setError(null)
      refreshSummary()
    } catch (err) {
      setError(toMessage(err))
    } finally {
      setBusy(false)
    }
  }, [getToken])

  useEffect(() => {
    void reload()
  }, [reload])

  const counts = useMemo(() => {
    const c = { all: 0, submitted: 0, quoted: 0, accepted: 0, closed: 0, overdue: 0 }
    for (const q of items ?? []) {
      c.all += 1
      c[q.status] += 1
      if (q.overdue) c.overdue += 1
    }
    return c
  }, [items])

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase()
    return (items ?? []).filter(
      (q) =>
        (tab === 'all' || q.status === tab) &&
        (!overdueOnly || q.overdue) &&
        quoteMatchesPaymentSource(q, source) &&
        (!needle ||
          [q.request.productName, q.request.contact.name, q.request.contact.email]
            .join(' ')
            .toLowerCase()
            .includes(needle)),
    )
  }, [items, tab, overdueOnly, source, search])
  const paged = usePagedList(visible)

  // 옛 링크(/quotes?id=…, 텔레그램·메일 알림)는 상세 페이지로 넘긴다
  const legacyId = params.get('id')
  if (legacyId) return <Navigate to={`/quotes/${encodeURIComponent(legacyId)}`} replace />

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="견적 요청"
        description={`사용자가 보낸 공장 견적 요청이에요. 접수 후 ${QUOTE_SLA_DAYS}일 안에 공장 견적을 1건 이상 넣어 주세요.`}
        actions={
          <Button
            variant="secondary"
            icon={<ArrowsClockwise />}
            loading={busy}
            onClick={() => void reload()}
          >
            새로고침
          </Button>
        }
      />

      <HelpPanel
        id="quotes"
        steps={[
          <>
            <b>견적 대기</b> 탭에서 요청을 누르면 상세 페이지가 열려요. 요청자 연락처와
            스펙시트(배합·공정)를 공장에 전달하세요.
          </>,
          <>
            공장에서 답이 오면 상세 페이지의 <b>공장 견적 추가</b>로 단가·MOQ·납기를 입력하고
            보내세요. 저장하는 순간 사용자에게 푸시가 가고 상태가 <b>견적 보냄</b>으로 바뀌어요.
            공장이 여러 곳이면 여러 건 넣으세요.
          </>,
          <>
            사용자가 앱에서 공장을 고르면 <b>공장 선택됨</b>이 되고 운영자에게 텔레그램·메일이 와요.
            공장과 사용자를 연결(샘플·발주)한 뒤 <b>마감</b>하세요.
          </>,
          <>
            진행 상황은 <b>운영 메모</b>에 남기고(사용자에게 안 보여요), 사용자에게 알릴 일은{' '}
            <b>요청자에게 메시지</b>로 보내세요(앱 알림 + 푸시).
          </>,
        ]}
        notes={[
          `접수 후 ${QUOTE_SLA_DAYS}일이 지나도 견적이 없으면 "지연" 배지가 붙고 사이드바 숫자에 계속 남아요.`,
          '견적을 입력·수정·삭제할 때마다 상세 페이지의 [견적 내역]에 그 시점의 값과 작업자가 남아요. 지운 견적도 내역에는 남아요.',
          '견적의 설명·메모는 요청자의 언어로 자동 번역돼 저장돼요. 번역 후에도 언어가 섞이면 저장이 거부돼요.',
          '사용자는 견적 1건당 크레딧 1개를 써요. 잘못 접수된 요청은 [사용자] 화면에서 크레딧을 돌려주세요.',
        ]}
      />

      {error && <Banner variant="error" title="문제가 생겼어요" description={error} />}

      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <Tabs
            variant="underline"
            size="sm"
            value={tab}
            onValueChange={(v) => setTab(v as typeof tab)}
            tabs={[
              { value: 'all', label: `전체 ${counts.all}` },
              { value: 'submitted', label: `견적 대기 ${counts.submitted}` },
              { value: 'quoted', label: `견적 보냄 ${counts.quoted}` },
              { value: 'accepted', label: `공장 선택됨 ${counts.accepted}` },
              { value: 'closed', label: `마감 ${counts.closed}` },
            ]}
          />
          <div className="flex flex-wrap items-center gap-4">
            <Select
              aria-label="결제 출처"
              size="sm"
              className="min-w-36"
              value={source}
              onValueChange={(v: string | null) => setSource((v ?? 'all') as typeof source)}
              items={PAYMENT_SOURCE_ITEMS}
            />
            <Switch
              label={`지연만 (${counts.overdue})`}
              checked={overdueOnly}
              onCheckedChange={(v: boolean) => setOverdueOnly(v)}
            />
            <div className="w-64">
              <Input
                aria-label="검색"
                placeholder="제품명 · 요청자 · 이메일"
                value={search}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
              />
            </div>
          </div>
        </div>

        <Surface className="w-full overflow-x-auto rounded-lg p-0">
          {items === null ? (
            <div className="flex items-center gap-3 p-6">
              <Loader />
              <Text variant="secondary">불러오는 중…</Text>
            </div>
          ) : visible.length === 0 ? (
            <Empty
              icon={<Receipt size={40} className="text-kumo-inactive" />}
              title={items.length === 0 ? '아직 견적 요청이 없어요' : '조건에 맞는 요청이 없어요'}
              description={
                items.length === 0
                  ? '사용자가 앱에서 견적을 요청하면 여기에 나타나고 텔레그램·메일로 알림이 와요.'
                  : '탭이나 검색어를 바꿔 보세요.'
              }
            />
          ) : (
            <Table className="w-full">
              <Table.Header variant="compact">
                <Table.Row>
                  <Table.Head>접수</Table.Head>
                  <Table.Head>제품</Table.Head>
                  <Table.Head>요청자</Table.Head>
                  <Table.Head>수량</Table.Head>
                  <Table.Head>목표 단가</Table.Head>
                  <Table.Head>공장 견적</Table.Head>
                  <Table.Head>최저 견적 단가</Table.Head>
                  <Table.Head>결제</Table.Head>
                  <Table.Head>상태</Table.Head>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {paged.slice.map((q) => {
                  const low = lowestOffer(q)
                  return (
                    <Table.Row
                      key={q.id}
                      data-testid="quote-row"
                      className="cursor-pointer"
                      onClick={() => navigate(`/quotes/${encodeURIComponent(q.id)}`)}
                    >
                      <Table.Cell className="whitespace-nowrap">
                        <div className="flex flex-col gap-0.5">
                          <span className="text-xs">{fmtTime(q.createdAt)}</span>
                          <AgeBadge q={q} />
                        </div>
                      </Table.Cell>
                      <Table.Cell>
                        <div className="font-medium">{q.request.productName}</div>
                        <div className="text-kumo-subtle text-xs">
                          {productCategoryLabel(q.request.categoryId)}
                        </div>
                      </Table.Cell>
                      <Table.Cell>
                        <div>{q.request.contact.name || '—'}</div>
                        <div className="text-kumo-subtle text-xs">{q.request.contact.email}</div>
                      </Table.Cell>
                      <Table.Cell className="whitespace-nowrap tabular-nums">
                        {fmtInt(q.request.quantity)}
                      </Table.Cell>
                      <Table.Cell className="whitespace-nowrap tabular-nums">
                        {q.request.targetUnitPrice === null
                          ? '—'
                          : fmtMoney(q.request.targetUnitPrice, q.request.currency)}
                      </Table.Cell>
                      <Table.Cell className="tabular-nums">{q.offers.length}건</Table.Cell>
                      <Table.Cell className="whitespace-nowrap tabular-nums">
                        {low ? fmtMoney(low.unitPrice, low.currency) : '—'}
                      </Table.Cell>
                      <Table.Cell>
                        <PaymentSourceBadge source={q.paymentSource} />
                      </Table.Cell>
                      <Table.Cell>
                        <Badge variant={STATUS_BADGE[q.status]}>{STATUS_LABEL[q.status]}</Badge>
                        <QuoteCloseBadge quote={q} />
                      </Table.Cell>
                    </Table.Row>
                  )
                })}
              </Table.Body>
            </Table>
          )}
        </Surface>
        <TablePagination
          page={paged.page}
          setPage={paged.setPage}
          perPage={paged.perPage}
          total={paged.total}
        />
      </div>
    </div>
  )
}
